import { useRef, useState, useCallback } from "react";
import { Upload, Camera, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAX_SIZE_PX = 1280; // longest side after resize
const QUALITY = 0.82;
const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

/** Resize an image file to a max bound and return base64 data URL. */
const resizeToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, MAX_SIZE_PX / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // jpeg compresses better for photos
        const out = canvas.toDataURL("image/jpeg", QUALITY);
        resolve({ data: out, type: "image/jpeg", name: (file.name || "photo.jpg").replace(/\.[^.]+$/, ".jpg") });
      };
      img.onerror = () => reject(new Error("Invalid image"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });

/**
 * Reusable image picker.
 *  - multiple: allow selecting multiple files
 *  - onPick(files): called with normalized {name, type, data}[] (resized data URLs)
 *  - compact: smaller variant (used inline in fault/maint dialogs)
 */
export const AssetImageUploader = ({ onPick, multiple = false, compact = false, label = "Upload image" }) => {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const arr = Array.from(files).filter(f => f.type.startsWith("image/"));
      if (arr.length === 0) throw new Error("Please choose image files only");
      const picked = [];
      for (const f of arr) {
        picked.push(await resizeToDataUrl(f));
        if (!multiple) break;
      }
      await onPick(picked);
    } catch (e) {
      toast.error(e.message || "Image upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  }, [onPick, multiple]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  if (compact) {
    return (
      <div className="flex gap-2 flex-wrap" data-testid="image-uploader-compact">
        <input ref={fileRef} type="file" accept={ACCEPT} multiple={multiple} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="image-file-input" />
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="image-camera-input" />
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="image-pick-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5 mr-1.5" />} Choose
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => camRef.current?.click()} data-testid="image-camera-btn">
          <Camera className="w-3.5 h-3.5 mr-1.5" /> Camera
        </Button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      className={
        "relative border-2 border-dashed rounded-xl p-6 text-center transition-all " +
        (drag ? "border-[#9DCD3F] bg-[#9DCD3F]/5" : "border-[#E2E8F0] bg-[#FDFBF7] hover:border-[#1B2C5A]/40")
      }
      data-testid="image-uploader"
    >
      <input ref={fileRef} type="file" accept={ACCEPT} multiple={multiple} className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="image-file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="image-camera-input" />
      {busy ? (
        <div className="flex flex-col items-center gap-2 py-4">
          <Loader2 className="w-7 h-7 text-[#1B2C5A] animate-spin" />
          <p className="text-sm text-[#475569]">Optimizing image…</p>
        </div>
      ) : (
        <>
          <div className="w-12 h-12 rounded-full bg-[#9DCD3F]/15 grid place-items-center mx-auto">
            <Upload className="w-5 h-5 text-[#1B2C5A]" />
          </div>
          <p className="mt-3 text-sm text-[#1B2C5A] font-medium">{label}</p>
          <p className="text-xs text-[#475569] mt-1">Drop a photo here, or pick a file. Images are auto-resized to {MAX_SIZE_PX}px.</p>
          <div className="mt-3 flex gap-2 justify-center flex-wrap">
            <Button type="button" size="sm" onClick={() => fileRef.current?.click()} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="image-pick-btn">
              <ImageIcon className="w-4 h-4 mr-1.5" /> Choose File
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => camRef.current?.click()} data-testid="image-camera-btn">
              <Camera className="w-4 h-4 mr-1.5" /> Use Camera
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/** Inline preview chip showing a thumbnail + remove button. */
export const ImageChip = ({ src, name, onRemove }) => (
  <div className="relative group inline-block">
    <img src={src} alt={name || "preview"} className="w-16 h-16 rounded-lg object-cover border border-[#E2E8F0]" loading="lazy" />
    {onRemove && (
      <button type="button" onClick={onRemove} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white grid place-items-center hover:bg-red-700 shadow" aria-label="Remove image">
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
);

export default AssetImageUploader;
